import type { EditorHintRegistry, FieldHintRecord } from "./types";

/**
 * Registr nápověd editoru zájezdu a hotelu (řez 10, typy v `./types`). Bez runtime importů:
 * měřidlo `scripts/kontrola/admin-napovedy.mjs` ho načítá přes `nactiTs`.
 *
 * Varianty: `hotel` = `isHotelTrip` (Pobyt + rezervace na vyžádání, web ho vykreslí na
 * stránce hotelu `routes.hotel`), `trip` = vše ostatní (`routes.tripDetail`). Varianta polí
 * se řídí hodnotami formuláře, odkazy na web uloženým řádkem (`TripEditPage`).
 *
 * - `showIn: ["trip"]` mají jen pole, která ve variantě hotel nic nečte (`heroTitle`,
 *   `guideVideoUrl`, `schedule`, `relatedItems`, `relatedPageIds`). Skryté pole zůstává ve
 *   formuláři i v těle uložení se svou načtenou hodnotou; `fieldVisible` řídí jen vykreslení.
 * - Kde se pole u hotelu chová jinak, říká to text varianty (`variantHintKeys.hotel`, klíč
 *   `<pole>Hotel`). Pole, které stránka hotelu nečte a slouží u hotelu jen back office
 *   (`itinerary`, `upgradeIds`), má u hotelu i druh `backoffice` (`hotelBackoffice`).
 * - Karty mimo formulář (`tripInspirationLinks`, `calculation`) ukládají svůj podzdroj samy;
 *   nápovědu vykresluje záložka pod kartou.
 * - Kotvy cílů záložek jsou literály hodnot `WEB_SECTIONS.trip` (měřidlo AK7 b je ověřuje
 *   proti `packages/shared/src/webSections.ts`). Stránka hotelu kotvy nemá, cíle hotelu vedou
 *   na stránku bez kotvy.
 */
const TRIP_DETAIL = "apps/web/src/components/pages/TripDetail.astro";
const HOTEL_DETAIL = "apps/web/src/components/pages/HotelDetail.astro";
const TRIP_CARD = "apps/web/src/components/TripCard.astro";
const QUICK_VIEW = "apps/web/src/components/trips/QuickViewModal.tsx";
const SEO_META = "apps/web/src/lib/seoMeta.ts";
const ROUTES = "packages/shared/src/routes.ts";
const TRIPS_LISTING = "apps/web/src/lib/tripsListing.ts";
const TRIP_PRESENTATION = "packages/shared/src/tripPresentation.ts";
const HOTEL_BOOKING = "apps/web/src/components/hotel/HotelBookingWidget.tsx";
const HOTEL_CARD = "apps/web/src/components/hotel/HotelCard.astro";
const INQUIRY_SECTION = "apps/web/src/components/trip/TripInquirySection.astro";
const INQUIRY_FORM = "apps/web/src/lib/tripInquiryForm.ts";
const INCLUDED_EXCLUDED = "apps/web/src/components/trip/IncludedExcluded.astro";
const OFFER_DOCUMENT = "apps/api/src/lib/offerDocument.ts";
const SIDEBAR_PANEL = "apps/web/src/components/trips/filters/SidebarPanel.tsx";
const GROUP_SIZE = "packages/shared/src/groupSize.ts";
const PUBLIC_TRIPS = "apps/api/src/routes/public/trips.ts";

/** Záznam druhu `web` s textem `webHints.trip.fields.<name>`. */
function web(
  name: string,
  readers: string[],
  extra: Partial<FieldHintRecord> = {},
): FieldHintRecord {
  return { kind: "web", hintKey: `webHints.trip.fields.${name}`, readers, ...extra };
}

/** Záznam druhu `backoffice` s textem `webHints.trip.fields.<name>`. */
function backoffice(
  name: string,
  readers: string[],
  extra: Partial<FieldHintRecord> = {},
): FieldHintRecord {
  return { kind: "backoffice", hintKey: `webHints.trip.fields.${name}`, readers, ...extra };
}

/** Text varianty hotel: `webHints.trip.fields.<name>Hotel`. */
function hotelText(name: string): Partial<FieldHintRecord> {
  return { variantHintKeys: { hotel: `webHints.trip.fields.${name}Hotel` } };
}

/** Varianta hotel jen pro back office: druh `backoffice` a text `webHints.trip.fields.<name>Hotel`. */
function hotelBackoffice(name: string): Partial<FieldHintRecord> {
  return { ...hotelText(name), variantKind: { hotel: "backoffice" } };
}

const TRIP_ONLY: Partial<FieldHintRecord> = { showIn: ["trip"] };

const LEGACY_IMAGE = "starý URL obrázku; web ho čte jako zálohu, editor ukládá média přes *Id";

export const tripHintRegistry: EditorHintRegistry = {
  editor: "trip",
  table: "trips",
  variants: ["trip", "hotel"],
  pages: { trip: "trip", hotel: "hotel" },
  fields: {
    // Záložka Základní
    title: web("title", [
      "apps/web/src/components/trip/TripHero.astro",
      TRIP_DETAIL,
      HOTEL_DETAIL,
      TRIP_CARD,
      SEO_META,
    ]),
    slug: web("slug", [ROUTES, TRIP_CARD]),
    destinationId: web("destinationId", [
      TRIP_CARD,
      "apps/web/src/components/destination/TripsCarousel.astro",
      "apps/web/src/pages/hotely/index.astro",
    ]),
    destinationTagIds: web("destinationTagIds", [
      "apps/web/src/components/home/tripsCarousel.ts",
      "apps/web/src/lib/hotelDestinationOptions.ts",
    ]),
    guideId: web(
      "guideId",
      [
        TRIP_DETAIL,
        "apps/web/src/components/pages/GuideDetail.astro",
        "apps/api/src/lib/tripFilterPipeline.ts",
      ],
      { readAs: ["guide"], ...hotelText("guideId") },
    ),
    guideVideoUrl: web("guideVideoUrl", [TRIP_DETAIL], TRIP_ONLY),
    difficultyLevel: web("difficultyLevel", [QUICK_VIEW, TRIP_DETAIL]),
    tripKind: web("tripKind", [ROUTES, TRIP_DETAIL, "apps/web/src/lib/departureSelection.ts"]),
    fulfillmentMode: web("fulfillmentMode", [
      "apps/web/src/lib/departureSelection.ts",
      "apps/web/src/lib/tripsMapPoints.ts",
      ROUTES,
    ]),
    tripType: web("tripType", [
      "apps/web/src/components/trip/TripTypeGuidePills.astro",
      TRIPS_LISTING,
    ]),
    countryCodesText: backoffice(
      "countryCodesText",
      [OFFER_DOCUMENT, "apps/api/src/lib/entryTasks.ts", "apps/api/src/lib/templateVars.ts"],
      { readAs: ["countryCodes"], columns: ["countryCodes"] },
    ),
    tripStyle: web("tripStyle", [TRIPS_LISTING, SIDEBAR_PANEL, "apps/web/src/lib/menuModel.ts"]),
    priceFrom: web("priceFrom", [TRIP_DETAIL, TRIP_CARD, HOTEL_DETAIL, HOTEL_BOOKING]),
    priceIsFrom: web("priceIsFrom", [
      TRIP_CARD,
      "apps/web/src/components/trip/BookingPanel.astro",
      INQUIRY_SECTION,
    ]),
    priceOriginal: web("priceOriginal", [TRIP_CARD, TRIP_DETAIL, HOTEL_DETAIL]),
    durationHours: web("durationHours", [
      "packages/shared/src/utils/index.ts",
      QUICK_VIEW,
      TRIP_CARD,
    ]),
    durationDays: web("durationDays", [
      TRIP_DETAIL,
      TRIP_CARD,
      HOTEL_DETAIL,
      "apps/web/src/components/SearchModal.tsx",
    ]),
    groupSizeMin: web("groupSizeMin", [QUICK_VIEW, GROUP_SIZE]),
    groupSizeMax: web("groupSizeMax", [
      QUICK_VIEW,
      GROUP_SIZE,
      "apps/api/src/lib/departureCapacity.ts",
    ]),
    groupSizeOpenEnded: web("groupSizeOpenEnded", [
      TRIP_DETAIL,
      TRIP_CARD,
      "apps/web/src/components/trip/DeparturesTable.astro",
    ]),
    defaultMeetingPoint: backoffice("defaultMeetingPoint", ["apps/api/src/lib/meeting.ts"]),
    defaultMeetingTime: backoffice("defaultMeetingTime", ["apps/api/src/lib/meeting.ts"]),
    shortDescription: web("shortDescription", [SEO_META, TRIP_CARD, HOTEL_DETAIL]),
    description: web("description", [
      TRIP_DETAIL,
      "apps/web/src/components/hotel/HotelFactSheet.astro",
      HOTEL_DETAIL,
    ]),
    heroTitle: web(
      "heroTitle",
      ["apps/web/src/components/trip/TripHero.astro", TRIP_DETAIL],
      TRIP_ONLY,
    ),
    heading: web("heading", [TRIP_DETAIL, HOTEL_DETAIL]),
    metaTitle: web("metaTitle", [SEO_META]),
    metaDescription: web("metaDescription", [SEO_META]),
    featuredImageId: web("featuredImageId", [TRIP_DETAIL, TRIP_CARD, HOTEL_DETAIL], {
      readAs: ["featuredImageMedia"],
    }),
    isFeatured: web("isFeatured", ["apps/web/src/pages/index.astro", TRIP_CARD, TRIP_PRESENTATION]),
    isLastMinute: web("isLastMinute", [TRIP_CARD, TRIP_PRESENTATION]),
    isForWomen: web("isForWomen", [TRIPS_LISTING, SIDEBAR_PANEL]),
    hasCzechGuide: web("hasCzechGuide", [
      "apps/web/src/components/trip/TripTypeGuidePills.astro",
      TRIP_CARD,
      TRIP_PRESENTATION,
    ]),
    tags: web("tags", [TRIP_DETAIL]),
    // Rezervace na vyžádání (Základní, pod režimem rezervace)
    leadTimeHours: web("leadTimeHours", [INQUIRY_SECTION, INQUIRY_FORM, HOTEL_BOOKING]),
    availableWeekdays: web("availableWeekdays", [INQUIRY_SECTION, INQUIRY_FORM, HOTEL_BOOKING]),
    // Záložka Zážitek (u hotelu, který není zážitek, `highlights` na záložce Hotel)
    highlights: web(
      "highlights",
      [TRIP_DETAIL, "apps/web/src/components/trip/TripHighlightsGrid.astro", HOTEL_DETAIL],
      hotelText("highlights"),
    ),
    schedule: web(
      "schedule",
      [TRIP_DETAIL, "apps/web/src/components/trip/TripSchedule.astro"],
      TRIP_ONLY,
    ),
    // Záložka Hotel
    hotelStars: web("hotelStars", [HOTEL_DETAIL, HOTEL_CARD, TRIPS_LISTING]),
    hotelMealPlans: web("hotelMealPlans", [HOTEL_DETAIL, HOTEL_BOOKING, HOTEL_CARD]),
    hotelDepartures: web("hotelDepartures", [HOTEL_DETAIL, HOTEL_BOOKING]),
    hotelFeatures: web("hotelFeatures", [HOTEL_DETAIL, "apps/api/src/lib/tripFilterPipeline.ts"]),
    hotelRooms: web("hotelRooms", [HOTEL_DETAIL, HOTEL_BOOKING]),
    hotelCoordinatesText: web(
      "hotelCoordinatesText",
      [HOTEL_DETAIL, "apps/api/src/lib/tripMapPoint.ts"],
      { readAs: ["hotelCoordinates"], columns: ["hotelLatitude", "hotelLongitude"] },
    ),
    // Záložka Co je v ceně
    included: web("included", [INCLUDED_EXCLUDED, TRIP_DETAIL, HOTEL_DETAIL]),
    excluded: web("excluded", [INCLUDED_EXCLUDED, TRIP_DETAIL, HOTEL_DETAIL]),
    note: web("note", [INCLUDED_EXCLUDED, TRIP_DETAIL, HOTEL_DETAIL]),
    optionalActivities: web("optionalActivities", [HOTEL_DETAIL, OFFER_DOCUMENT]),
    // Záložka Itinerář
    itinerary: web(
      "itinerary",
      [
        TRIP_DETAIL,
        "apps/web/src/components/trip/TripItinerary.astro",
        OFFER_DOCUMENT,
        "apps/guide/src/App.tsx",
      ],
      hotelBackoffice("itinerary"),
    ),
    // Záložka Galerie
    galleryImageIds: web("galleryImageIds", [TRIP_DETAIL, HOTEL_DETAIL], {
      readAs: ["galleryMedia"],
    }),
    youtubeVideos: web("youtubeVideos", [TRIP_DETAIL, HOTEL_DETAIL]),
    // Záložka Termíny
    dates: web("dates", ["apps/web/src/components/trip/DeparturesTable.astro", TRIP_DETAIL]),
    // Záložka Komponenty
    components: backoffice(
      "components",
      ["apps/api/src/routes/admin/trips.ts", "apps/api/src/lib/tripComponentsSeed.ts"],
      { readAs: ["tripComponents"] },
    ),
    // Záložka Obsah
    faqs: web("faqs", [TRIP_DETAIL, HOTEL_DETAIL]),
    socialReels: web("socialReels", [TRIP_DETAIL, HOTEL_DETAIL]),
    mapImageId: web(
      "mapImageId",
      [TRIP_DETAIL, "apps/web/src/components/trips/TripsListing.astro"],
      { readAs: ["mapImageMedia"], ...hotelText("mapImageId") },
    ),
    guideRecommendation: web("guideRecommendation", [TRIP_DETAIL, HOTEL_DETAIL]),
    relatedItems: web("relatedItems", ["apps/web/src/lib/similarTripsRail.ts", PUBLIC_TRIPS], {
      readAs: ["manualSimilarTrips"],
      ...TRIP_ONLY,
    }),
    upgradeIds: web(
      "upgradeIds",
      [
        "apps/web/src/components/content/UpgradesRow.astro",
        TRIP_DETAIL,
        "apps/api/src/lib/orderItemsSuggest.ts",
      ],
      { readAs: ["upgrades"], ...hotelBackoffice("upgradeIds") },
    ),
    relatedPageIds: web("relatedPageIds", [TRIP_DETAIL], {
      readAs: ["relatedPages"],
      ...TRIP_ONLY,
    }),
    // Hlavička: brána publikace. Stav čte veřejné API, web dostává jen publikované zájezdy
    // a identifikátor sám nečte (výjimka `ctenar` v `admin-napovedy-vyjimky/trip.json`).
    status: web("status", [PUBLIC_TRIPS]),
    // Karty mimo formulář
    tripInspirationLinks: web(
      "tripInspirationLinks",
      ["apps/web/src/lib/inspirationTripGroups.ts", "apps/api/src/routes/public/activities.ts"],
      { outsideForm: true, readAs: ["linkedTrips"] },
    ),
    calculation: backoffice(
      "calculation",
      ["apps/api/src/routes/admin/calculations.ts", OFFER_DOCUMENT],
      { outsideForm: true, readAs: ["calculations"] },
    ),
  },
  columnsOutsideEditor: {
    id: { status: "system", reason: "technické" },
    createdAt: { status: "system", reason: "technické" },
    updatedAt: { status: "system", reason: "technické" },
    featuredImage: { status: "system", reason: LEGACY_IMAGE },
    galleryImages: { status: "system", reason: LEGACY_IMAGE },
    mapImage: { status: "system", reason: LEGACY_IMAGE },
    titleSk: {
      status: "bezPolicka",
      reason:
        "slovenská verze webu není; veřejné API sloupec vydává v projekci karet (tripColumns.ts), admin API ve výpisu a při duplikaci; web ho nečte a editor pole nemá",
    },
    shortDescriptionSk: { status: "necte", reason: "slovenská verze webu není; nikdo nečte" },
    descriptionSk: { status: "necte", reason: "slovenská verze webu není; nikdo nečte" },
    priceEur: {
      status: "bezPolicka",
      reason:
        "cena v EUR; veřejné API ji vydává v projekci karet (tripColumns.ts), web ji nečte a editor pole nemá",
    },
    startLocationId: {
      status: "bezPolicka",
      reason: "harmonogram průvodců (guideSchedule.ts, scheduleWindow.ts); web je nečte",
    },
    endLocationId: {
      status: "bezPolicka",
      reason: "harmonogram průvodců (guideSchedule.ts, scheduleWindow.ts); web je nečte",
    },
    sortOrder: { status: "jinde", reason: "Pořadí zájezdů (TripOrderPage)" },
    allowsOnlineBooking: { status: "necte", reason: "nikdo nečte; z editoru odebráno v řezu 10" },
  },
  tabs: {
    basic: { trip: { anchor: "popis" }, hotel: { anchor: null } },
    itinerary: {
      trip: { anchor: "itinerar" },
      hotel: { backoffice: "webHints.trip.tabs.itineraryHotel" },
    },
    experience: { trip: { anchor: "co-zazijete" }, hotel: { anchor: null } },
    hotel: { trip: { backoffice: "webHints.trip.tabs.hotelTrip" }, hotel: { anchor: null } },
    included: { trip: { anchor: "v-cene" }, hotel: { anchor: null } },
    gallery: { trip: { anchor: "galerie" }, hotel: { anchor: null } },
    // Hotel je na vyžádání, záložku Termíny nemá.
    dates: { trip: { anchor: "terminy" }, hotel: { notShown: true } },
    components: {
      trip: { backoffice: "webHints.trip.tabs.components" },
      hotel: { backoffice: "webHints.trip.tabs.components" },
    },
    calculation: {
      trip: { backoffice: "webHints.trip.tabs.calculation" },
      hotel: { backoffice: "webHints.trip.tabs.calculation" },
    },
    // Sekce mapy je na stránce zájezdu vždy (i s placeholderem).
    content: { trip: { anchor: "mapa" }, hotel: { anchor: null } },
  },
};
